/**
 * „Meine Leads"-Kachel (Jun-2026, User-Wunsch).
 *
 * Bringt die zugewiesenen Leads (aus dem Header-Dropdown „Meine Leads" /
 * Seite `/dealer/leads`) auch ins Händler-Dashboard: kompakte Statusübersicht
 * (Neu zugewiesen · In Bearbeitung · Gewonnen · Verloren) mit Schnell-Link zur
 * vollständigen Lead-Liste.
 *
 * Datenquelle: ``GET /api/dealer/leads`` → { items, counts }.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, ArrowRight } from "lucide-react";
import api from "../../lib/api";

const STATUS = [
    { key: "assigned", label: "Neu zugewiesen", cls: "text-blue-700" },
    { key: "in_progress", label: "In Bearbeitung", cls: "text-indigo-700" },
    { key: "won", label: "Gewonnen", cls: "text-emerald-700" },
    { key: "lost", label: "Verloren", cls: "text-gray-500" },
];

export default function MyLeadsCard({ user }) {
    const [counts, setCounts] = useState(null);
    const [alertCount, setAlertCount] = useState(0);
    const isEmp = user?.role === "zweiradverkaeufer";

    useEffect(() => {
        if (!user || (user.role !== "b2b" && !isEmp)) return;
        api.get("/dealer/leads", { params: isEmp ? { mine: 1 } : {} })
            .then((r) => setCounts(r.data?.counts || {}))
            .catch(() => setCounts(null));
        // Jul-2026 (Ampel-System): ungelesene Lead-Erinnerungen im Dashboard anzeigen
        api.get("/dealer/leads/alerts")
            .then((r) => setAlertCount((r.data?.items || []).length))
            .catch(() => {});
    }, [user, isEmp]);

    if (!user || (user.role !== "b2b" && !isEmp) || !counts) return null;
    const total = STATUS.reduce((s, x) => s + (counts[x.key] || 0), 0);

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="my-leads-card">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5">
                    <Inbox className="w-4 h-4 text-[#FFCC00]" />
                    <div className="text-[11px] uppercase tracking-wider font-bold text-gray-700">Meine Leads</div>
                </div>
                <Link to="/dealer/leads" className="text-[11px] font-bold text-[#111827] hover:underline inline-flex items-center gap-1" data-testid="my-leads-open">
                    Alle ansehen <ArrowRight className="w-3 h-3" />
                </Link>
            </div>
            {alertCount > 0 && (
                <Link to="/dealer/leads" className="mb-2 flex items-center gap-2 rounded-sm border-2 border-red-400 bg-red-50 px-3 py-2 text-xs font-bold text-red-800 hover:bg-red-100" data-testid="my-leads-ampel-alert">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-600 animate-pulse" />
                    {alertCount} Lead-Ampel-Erinnerung{alertCount > 1 ? "en" : ""} — jetzt reagieren
                </Link>
            )}
            {total === 0 ? (
                <p className="text-[12px] text-gray-500" data-testid="my-leads-empty">
                    Aktuell keine zugewiesenen Leads. Zugewiesene Angebote aus Autobörsen erscheinen hier und unter „Meine Leads".
                </p>
            ) : (
                <div className="grid grid-cols-2 gap-2" data-testid="my-leads-counts">
                    {STATUS.map((s) => (
                        <Link
                            key={s.key}
                            to="/dealer/leads"
                            className="border border-gray-200 rounded-sm px-3 py-2 hover:border-[#111827] transition-colors"
                            data-testid={`my-leads-kpi-${s.key}`}
                        >
                            <div className={`text-2xl font-black font-mono-num ${s.cls}`}>{counts[s.key] || 0}</div>
                            <div className="text-[9px] uppercase tracking-wider text-gray-500 font-bold mt-0.5">{s.label}</div>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}
